import React, { useEffect, useState } from "react";
import { Breadcrumb, Col, Form, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import userService from "../../app/services/user.service";
import Card from "../../UI/Card/Card";
import Graybg from "../../UI/Graybg";
import Loader from "../../UI/loader/loader";
import SecBTN from "../../UI/SecBTN";
import Select from "../../UI/Select/Select";
import Toast from "../../UI/Toast/Toast";

const StudentAttendance = () => {
  const [loading, setLoading] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);

  const [rawData, setRawData] = useState([]);
  const [classList, setClassList] = useState([]);
  const [batchList, setBatchList] = useState([]);
  const [studentList, setStudentList] = useState([]);

  const [selectedClass, setSelectedClass] = useState("");
  const [selectedBatch, setSelectedBatch] = useState("");
  const [attendanceDate, setAttendanceDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  // { [rollno]: "present" | "absent" | null }
  const [attendance, setAttendance] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [hasExistingAttendance, setHasExistingAttendance] = useState(false);

  const getKey = (s) =>
    String(s.student_rollno || s.student_id || s.s_roll_no || "");

  // How many students have been marked (either present or absent)
  const markedCount = Object.values(attendance).filter(
    (v) => v === "present" || v === "absent"
  ).length;
  const allMarked =
    studentList.length > 0 && markedCount === studentList.length;

  useEffect(() => {
    setLoading(true);
    userService
      .GetClassBatchList()
      .then((res) => {
        setLoading(false);
        const sessions = res?.data || [];
        setRawData(sessions);
        const seen = new Set();
        const allClasses = [];
        sessions.forEach((session) => {
          (session.classes || []).forEach((cls) => {
            if (!seen.has(cls.class)) {
              seen.add(cls.class);
              allClasses.push(cls.class);
            }
          });
        });
        setClassList(allClasses);
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
        toast.error("Failed to load class list.");
      });
  }, []);

  const onSelectClass = (e) => {
    const className = e.target.value;
    setSelectedClass(className);
    setSelectedBatch("");
    setStudentList([]);
    setAttendance({});
    setSubmitted(false);

    if (!className) return;

    const seen = new Set();
    const batches = [];
    rawData.forEach((session) => {
      (session.classes || []).forEach((cls) => {
        if (cls.class === className) {
          (cls.batches || []).forEach((b) => {
            if (!seen.has(b)) {
              seen.add(b);
              batches.push(b);
            }
          });
        }
      });
    });
    setBatchList(batches);
  };

  const onSelectBatch = (e) => {
    setSelectedBatch(e.target.value);
    setStudentList([]);
    setAttendance({});
    setSubmitted(false);
  };

  const onSearch = () => {
    if (!selectedBatch) {
      toast.warning("Please select a batch first.");
      return;
    }
    setLoading(true);
    setSubmitted(false);
    userService
      .batchWiseStudentList({
        batch_name: selectedBatch,
        attendance_date: attendanceDate,
      })
      .then((data) => {
        setLoading(false);
        const students = data?.data?.student_data || data?.data || [];
        if (Array.isArray(students) && students.length > 0) {
          setStudentList(students);
          // Pre-fill if attendance already exists
          const initial = {};
          students.forEach((s) => {
            const key = getKey(s);
            const status = s.class_status?.toLowerCase();
            initial[key] =
              status === "present" || status === "absent" ? status : null;
          });


          setAttendance(initial);
          const hasExisting = Object.values(initial).some(
            (status) => status === "present" || status === "absent"
          );

          setHasExistingAttendance(hasExisting);

        } else {
          setStudentList([]);
          setAttendance({});
          toast.info("No students found for this batch.");
        }
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
        toast.error("Failed to load student list.");
      });
  };

  // Mark a student present or absent; clicking the same status again clears it
  const mark = (key, status) => {
    if (submitted) return;
    setAttendance((prev) => ({
      ...prev,
      [key]: prev[key] === status ? null : status,
    }));
  };

  const markAll = (status) => {
    if (submitted) return;
    const next = {};
    studentList.forEach((s) => {
      next[getKey(s)] = status;
    });
    setAttendance(next);
  };

  const onSubmit = () => {
    if (!allMarked) {
      toast.warning("Please mark attendance for all students before submitting.");
      return;
    }
    setBtnLoading(true);

    const body = {
      attendance_date: attendanceDate,
      class: selectedClass,
      batch: selectedBatch,
      students: studentList.map((s) => ({
        student_rollno: getKey(s),
        status: attendance[getKey(s)],
      })),
    };

    userService
      .submitAttendance(body)
      .then(() => {
        setBtnLoading(false);
        setSubmitted(true);
        toast.success("Attendance submitted successfully.");
      })
      .catch((err) => {
        setBtnLoading(false);
        console.log(err);
        toast.error("Failed to submit attendance.");
      });
  };

  const presentCount = Object.values(attendance).filter(
    (v) => v === "present"
  ).length;
  const absentCount = Object.values(attendance).filter(
    (v) => v === "absent"
  ).length;

  return (
    <div className="section">
      {loading && <Loader />}
      <Toast />

      <div className="d-flex justify-content-between align-items-center">
        <div>
          <h4>Student Attendance</h4>
          <Breadcrumb>
            <Breadcrumb.Item active>Student Attendance</Breadcrumb.Item>
          </Breadcrumb>
        </div>
      </div>

      <Graybg>
        {/* Filters */}
        <Card className="mb-4">
          <Row className="g-3 align-items-end">
            <Col sm={12} md={3}>
              <Form.Label>Class :</Form.Label>
              <Select value={selectedClass} onChange={onSelectClass}>
                <option value="">Select Class</option>
                {classList.map((cls) => (
                  <option key={cls} value={cls}>
                    Class {cls}
                  </option>
                ))}
              </Select>
            </Col>

            <Col sm={12} md={3}>
              <Form.Label>Batch :</Form.Label>
              <Select
                value={selectedBatch}
                onChange={onSelectBatch}
                disabled={!selectedClass}
              >
                <option value="">Select Batch</option>
                {batchList.map((batch) => (
                  <option key={batch} value={batch}>
                    {batch}
                  </option>
                ))}
              </Select>
            </Col>

            <Col sm={12} md={3}>
              <Form.Label>Date :</Form.Label>
              <input
                type="date"
                className="form-control"
                value={attendanceDate}
                onChange={(e) => setAttendanceDate(e.target.value)}
                style={{
                  border: "1px solid #dee2e6",
                  borderRadius: "6px",
                  padding: "8px 12px",
                  fontSize: "14px",
                  width: "100%",
                  outline: "none",
                }}
              />
            </Col>

            <Col sm={12} md={3}>
              <SecBTN
                className="sec-btn w-100"
                onClick={onSearch}
                disabled={!selectedBatch}
              >
                Search Students
              </SecBTN>
            </Col>
          </Row>
        </Card>

        {/* Student List */}
        {studentList.length > 0 && (
          <Card>
            {/* Header row */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">
                Mark Attendance &nbsp;
                <span className="text-muted" style={{ fontSize: "13px" }}>
                  ({markedCount} / {studentList.length} marked)
                </span>
              </h6>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-sm btn-outline-success"
                  onClick={() => markAll("present")}
                  disabled={submitted}
                >
                  All Present
                </button>
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => markAll("absent")}
                  disabled={submitted}
                >
                  All Absent
                </button>
              </div>
            </div>

            <div
              style={{
                maxHeight: "420px",
                overflowY: "auto",
                border: "1px solid #e9ecef",
                borderRadius: "8px",
              }}
            >
              <table className="table table-hover mb-0">
                <thead
                  style={{
                    position: "sticky",
                    top: 0,
                    background: "#f8f9fa",
                    zIndex: 1,
                  }}
                >
                  <tr>
                    <th style={{ width: "50px" }}>#</th>
                    <th className="text-center" style={{ width: "100px" }}>
                      Present
                    </th>
                    <th className="text-center" style={{ width: "100px" }}>
                      Absent
                    </th>
                    <th>Roll No.</th>
                    <th>Student Name</th>
                    <th>Faculty Name</th>

                  </tr>
                </thead>
                <tbody>
                  {studentList.map((student, index) => {
                    const key = getKey(student);
                    const status = attendance[key]; // "present" | "absent" | null
                    return (
                      <tr
                        key={key || index}
                        style={{
                          background:
                            status === "present"
                              ? "#d1f5d3"
                              : status === "absent"
                                ? "#fde8e8"
                                : "",
                          transition: "background 0.2s",
                        }}
                      >
                        <td>{index + 1}</td>
                        <td className="text-center">
                          <input
                            type="checkbox"
                            checked={status === "present"}
                            onChange={() => mark(key, "present")}
                            style={{
                              width: "18px",
                              height: "18px",
                              cursor: submitted ? "not-allowed" : "pointer",
                              accentColor: "#28a745",
                            }}
                          />
                        </td>

                        {/* Absent — custom cross box */}
                        <td className="text-center">
                          <div
                            onClick={() => !submitted && mark(key, "absent")}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "18px",
                              height: "18px",
                              border: `2px solid ${status === "absent" ? "#dc3545" : "#adb5bd"}`,
                              borderRadius: "3px",
                              background: status === "absent" ? "#dc3545" : "#fff",
                              cursor: submitted ? "not-allowed" : "pointer",
                              transition: "all 0.15s ease",
                              flexShrink: 0,
                            }}
                          >
                            {status === "absent" && (
                              <span
                                style={{
                                  color: "#fff",
                                  fontSize: "12px",
                                  fontWeight: "bold",
                                  lineHeight: 1,
                                  userSelect: "none",
                                }}
                              >
                                ✕
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          {student.student_rollno ||
                            student.roll_no ||
                            "—"}
                        </td>
                        <td>
                          {student.student_name ||
                            student.s_name ||
                            "Unknown"}
                        </td>
                        <td>{student.faculty_name || "—"}</td>

                        {/* Present checkbox — tick */}

                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Summary */}
            <div
              className="d-flex gap-3 mt-3"
              style={{ fontSize: "13px", color: "#6c757d" }}
            >
              <span>
                Total:{" "}
                <strong style={{ color: "#212529" }}>
                  {studentList.length}
                </strong>
              </span>
              <span>
                Present:{" "}
                <strong style={{ color: "#28a745" }}>{presentCount}</strong>
              </span>
              <span>
                Absent:{" "}
                <strong style={{ color: "#dc3545" }}>{absentCount}</strong>
              </span>
              <span>
                Pending:{" "}
                <strong style={{ color: "#fd7e14" }}>
                  {studentList.length - markedCount}
                </strong>
              </span>
            </div>

            {/* Progress bar */}
            <div
              className="mt-2"
              style={{
                height: "6px",
                background: "#e9ecef",
                borderRadius: "4px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${studentList.length
                      ? (markedCount / studentList.length) * 100
                      : 0
                    }%`,
                  background: allMarked ? "#28a745" : "#0d6efd",
                  transition: "width 0.3s ease",
                  borderRadius: "4px",
                }}
              />
            </div>

            {/* Submit */}
            <div className="text-center mt-4 mb-2">
              <SecBTN
                className="sec-btn1"
                onClick={onSubmit}
                disabled={btnLoading || !allMarked || submitted || hasExistingAttendance}
              >
                {btnLoading
                  ? "Submitting..."
                  : hasExistingAttendance
                    ? "Attendance Already Submitted"
                    : submitted
                      ? "Attendance Submitted ✓"
                      : allMarked
                        ? "Submit Attendance"
                        : `Submit Attendance (${studentList.length - markedCount} pending)`}
              </SecBTN>
            </div>
          </Card>
        )}
      </Graybg>
    </div>
  );
};

export default StudentAttendance;
